import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import StatusBadge from '../../../components/StatusBadge';
import { useToast } from '../../../components/Toast';
import { Episode, Project } from '../../../types';
import { getEpisodeProgressLabel } from '../../project-detail/utils/episodeProgress';
import { projectEpisodePath } from '../../../utils/routes';

interface EpisodeNavSidebarProps {
  episodes: Episode[];
  currentEpisodeId?: string;
  project: Project;
  analysisStatus?: string;
  onRetryFailed: () => void;
  onNormalizeAll: () => void;
  onRegenAllPrompts: () => void;
  onRetryPronouns: () => void;
  onNavigate?: () => void;
}

export default function EpisodeNavSidebar({
  episodes,
  currentEpisodeId,
  project,
  analysisStatus,
  onRetryFailed,
  onNormalizeAll,
  onRegenAllPrompts,
  onRetryPronouns,
  onNavigate,
}: EpisodeNavSidebarProps) {
  const { t } = useTranslation('projectDetail');
  const navigate = useNavigate();
  const toast = useToast();
  const [menuOpen, setMenuOpen] = useState(false);
  const [jumpValue, setJumpValue] = useState('');

  const handleJump = () => {
    const raw = jumpValue.trim();
    if (!raw) return;
    const n = Number(raw);
    if (!Number.isInteger(n)) return;
    const target = episodes.find(ep => ep.index === n);
    if (!target) {
      toast.error(t('episodes.jumpNotFound', { n: raw }));
      return;
    }
    setJumpValue('');
    navigate(projectEpisodePath(project.id, target.id));
    onNavigate?.();
  };

  // 进入/切集时把当前集在分集列表内居中滚入（仅滚动列表内部，不影响页面滚动）
  const listRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLAnchorElement>(null);
  useEffect(() => {
    const list = listRef.current;
    const item = activeRef.current;
    if (!list || !item) return;
    const lr = list.getBoundingClientRect();
    const ir = item.getBoundingClientRect();
    const target = list.scrollTop + (ir.top - lr.top) - (list.clientHeight - item.clientHeight) / 2;
    list.scrollTop = Math.max(0, target);
  }, [currentEpisodeId, episodes.length]);

  const failedPromptCount = episodes.filter(ep => ep.promptGenerationStatus === 'failed').length;
  const failedPronounCount = episodes.filter(ep => ep.status === 'resolving_pronouns_failed').length;
  const hasFailures = failedPromptCount > 0 || failedPronounCount > 0;
  const hasEpisodes = episodes.length > 0;

  const actions: { key: string; label: string; onClick: () => void; warn?: boolean }[] = [];
  if (hasEpisodes) actions.push({ key: 'regen', label: t('episodes.regenAllPrompts'), onClick: onRegenAllPrompts });
  if (project.enableScriptNormalization) actions.push({ key: 'normalize', label: t('episodes.reNormalize'), onClick: onNormalizeAll });
  if (failedPromptCount > 0) actions.push({ key: 'retry', label: t('episodes.retryFailedEpisodes', { count: failedPromptCount }), onClick: onRetryFailed, warn: true });
  if (failedPronounCount > 0) actions.push({ key: 'pronoun', label: `${failedPronounCount} 集格式改写重试`, onClick: onRetryPronouns, warn: true });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.6rem', position: 'relative', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--theme-storyboard)' }}>{t('episodes.title')}</span>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-2)', flexShrink: 0 }}>{t('episodes.jumpTo')}</span>
          <input
            type="text"
            inputMode="numeric"
            value={jumpValue}
            onChange={e => setJumpValue(e.target.value.replace(/[^0-9]/g, ''))}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleJump(); } }}
            title={t('episodes.jumpTo')}
            style={{
              width: '40px', flexShrink: 0, padding: '2px 6px', fontSize: '0.72rem',
              borderRadius: '6px', border: '1px solid var(--border)',
              background: 'var(--surface)', color: 'var(--text)', outline: 'none',
            }}
          />
          <span style={{ fontSize: '0.72rem', color: 'var(--text-2)', flexShrink: 0 }}>{t('episodes.jumpUnit')}</span>
        </div>
        {actions.length > 0 && (
          <>
            <button
              onClick={() => setMenuOpen(v => !v)}
              title={t('episodes.title')}
              style={{
                position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: '24px', height: '24px', borderRadius: '6px', flexShrink: 0,
                background: menuOpen ? 'var(--surface-2)' : 'transparent', border: '1px solid var(--border)',
                color: 'var(--text-2)', cursor: 'pointer', padding: 0,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
              </svg>
              {hasFailures && (
                <span style={{ position: 'absolute', top: '-3px', right: '-3px', width: '7px', height: '7px', background: 'var(--danger)', borderRadius: '50%' }} />
              )}
            </button>
            {menuOpen && (
              <>
                <div onClick={() => setMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 89 }} />
                <div style={{
                  position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 90,
                  display: 'flex', flexDirection: 'column', gap: '2px', minWidth: '160px',
                  background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px',
                  boxShadow: 'var(--shadow)', padding: '4px',
                }}>
                  {actions.map(action => (
                    <button
                      key={action.key}
                      onClick={() => { action.onClick(); setMenuOpen(false); }}
                      style={{
                        textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer',
                        padding: '6px 10px', borderRadius: '6px', fontSize: '0.78rem', whiteSpace: 'nowrap',
                        color: action.warn ? 'var(--warning)' : 'var(--text)',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-2)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>

      <div ref={listRef} className="thin-scroll" style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: '8px' }}>
        {episodes.map(ep => {
          const isCurrent = ep.id === currentEpisodeId;
          return (
            <Link
              key={ep.id}
              ref={isCurrent ? activeRef : undefined}
              to={projectEpisodePath(project.id, ep.id)}
              onClick={onNavigate}
              style={{
                display: 'block', textDecoration: 'none', color: 'inherit',
                padding: '7px 9px', borderRadius: '7px',
                border: `1px solid ${isCurrent ? 'var(--theme-storyboard)' : 'var(--border)'}`,
                background: isCurrent ? 'color-mix(in srgb, var(--theme-storyboard) 12%, var(--surface))' : 'var(--surface)',
                transition: 'all 0.15s',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '5px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', minWidth: 0 }}>
                  {(ep.pendingCommentCount ?? 0) > 0 && (
                    <span style={{ width: '7px', height: '7px', background: '#ef4444', borderRadius: '50%', flexShrink: 0 }} />
                  )}
                  <span style={{ fontSize: '0.82rem', fontWeight: isCurrent ? 700 : 600 }}>EP{ep.index}</span>
                </div>
                {/* 已完成（分镜全部产出）不再显示状态胶囊——无胶囊即代表已完成 */}
                {ep.status !== 'completed' && (
                  <StatusBadge
                    status={ep.status}
                    label={getEpisodeProgressLabel(ep, project, analysisStatus, t)}
                    style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem', whiteSpace: 'nowrap', flexShrink: 0 }}
                  />
                )}
              </div>
              {(ep.totalShots ?? 0) > 0 && (
                <div style={{ fontSize: '0.72rem', color: 'var(--text-2)', marginTop: '5px' }}>
                  {t('episodes.shots', { completed: ep.completedShots, total: ep.totalShots })}
                </div>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
